// src/TrainingLibrary/localLibrary.js
// Plantillas locales + helpers para convertirlas al payload del backend.

const toMinutes = (hms = '') => {
  if (!hms || typeof hms !== 'string') return 0;
  if (!hms.includes(':')) return Number(hms) || 0;
  const [h, m, s] = hms.split(':').map(Number);
  return (h || 0) * 60 + (m || 0) + Math.round((s || 0) / 60);
};

const toHMS = (min) => {
  const total = Math.round((min || 0) * 60);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

// Mapear a tipos válidos de Activity.type (backend en ES)
export const mapSportEs = (sport = '') => {
  const s = String(sport).toLowerCase();
  if (['correr', 'run', 'running'].includes(s)) return 'correr';
  if (['ciclismo', 'bike', 'cycling'].includes(s)) return 'ciclismo';
  if (['nadar', 'swim', 'swimming'].includes(s)) return 'nadar';
  if (['fuerza', 'strength', 'gym'].includes(s)) return 'fuerza';
  if (['descanso', 'rest'].includes(s)) return 'descanso';
  if (['caminar', 'walk', 'walking'].includes(s)) return 'caminar';
  return 'otro';
};

// === Paleta/tema TrainingPeaks-like (unificada) ===
export const SPORT_THEME = {
  correr:   { color: '#2176FF', icon: 'fa icon-tzone-run' },       // azul
  ciclismo: { color: '#F59E0B', icon: 'fa icon-tzone-bike' },      // ámbar
  nadar:    { color: '#0891B2', icon: 'fa icon-tzone-swim' },      // cian
  fuerza:   { color: '#7C3AED', icon: 'fa icon-tzone-strength' },  // violeta
  descanso: { color: '#94A3B8', icon: 'fa icon-tzone-rest' },      // gris
  caminar:  { color: '#475569', icon: 'fa icon-tzone-walk' },      // slate
  otro:     { color: '#64748B', icon: 'fa icon-tzone-other' },     // gris oscuro
};

export const getSportTheme = (sport) => {
  const key = mapSportEs(sport);
  return SPORT_THEME[key] || SPORT_THEME.otro;
};

// Título visible por deporte (por si el front no lo setea)
const sportTitle = (sport = '') => {
  const s = mapSportEs(sport);
  switch (s) {
    case 'correr': return 'Correr';
    case 'ciclismo': return 'Ciclismo';
    case 'nadar': return 'Nadar';
    case 'fuerza': return 'Fuerza';
    case 'descanso': return 'Descanso';
    case 'caminar': return 'Caminar';
    default: return 'Otro';
  }
};

// Empaquetamos las entradas en el formato que usa el panel (name/sport/expected/ui)
const mapRun = (obj, defaults = {}) => ({
  _id: `local-${Math.random().toString(36).slice(2)}`,
  name: obj.title,
  sport: mapSportEs('correr'),
  intensity: defaults.intensity || 'base',
  expected: {
    tss: obj.tss ? Number(obj.tss) : (defaults.tss ?? 50),
    durationMin: obj.duration
      ? toMinutes(obj.duration)
      : (obj.distance ? Math.round(Number(obj.distance) * 6) : (defaults.durationMin ?? 60)),
  },
  description: obj.description || '',
  ui: {
    // Si la plantilla trae su propio icon/bg_color se respetan;
    // si no, usamos el tema unificado para "correr".
    icon: obj.icon || getSportTheme('correr').icon,
    bgColor: (obj.bg_color || defaults.bgColor || getSportTheme('correr').color).trim(),
  },
  __raw: obj, // guardamos original por si quieres mapear 1:1
});

/* ============================
 *    BASE AERÓBICA (7)
 * ============================ */
const baseAerobica = () => [
  mapRun({
    title: 'Base aeróbica',
    duration: '',
    distance: 14,
    description:
      'Descripción: completa la distancia usando los primeros 15min para progresar lentamente de zona 1 de FC a zona 2. Acumula lo más que puedas en zona 2.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Piques 30s en subida',
    duration: '1:00:00',
    distance: '',
    description:
      'Descripción: calienta bien por 30min, haciendo estiramientos dinámicos y 2K suaves. Luego en subida moderada (5-7%), completa 8 piques de 30 segundos al paso máximo sostenible. Luego corre 2K de enfriamiento. La recuperación entre cada pique es de 3min (incluyendo trote suave hasta volver al punto de origen).',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Base aeróbica + 10 x 150s',
    duration: '',
    distance: 14,
    description:
      'Descripción: empieza con 10K en zonas 1-2 de FC, progresando lentamente a zona 2. Luego completa 2 series de 5 x (150m duros@ capacidad anaeróbica + 250m suaves recuperando), descansando 4min entre series.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Base aeróbica + 8 x 150s',
    duration: '',
    distance: 13.2,
    description:
      'Descripción: empieza con 10K en zonas 1-2 de FC, progresando lentamente a zona 2. Luego completa 2 series de 4 x (150m duros@ capacidad anaeróbica + 250m suaves recuperando), descansando 4min entre series.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Base aeróbica + piques 12s + 6 x 100s',
    duration: '',
    distance: 11,
    description:
      'Descripción: empieza con 10K en zonas 1-2 de FC, progresando lentamente a zona 2. Luego 6 x 12seg al máximo en subida fuerte (>10%) + 6 x 100m planos @ capacidad anaeróbica.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Base aeróbica + 8 x 200s',
    duration: '',
    distance: 13.2,
    description:
      'Descripción: empieza con 10K en zonas 1-2 de FC, progresando lentamente a zona 2. Luego completa 2 series de 4 x (200m duros@ capacidad anaeróbica + 200m suaves recuperando), descansando 4min entre series.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
  mapRun({
    title: 'Base aeróbica + 10 x 200s',
    duration: '',
    distance: 14,
    description:
      'Descripción: empieza con 10K en zonas 1-2 de FC, progresando lentamente a zona 2. Luego completa 2 series de 5 x (200m duros@ capacidad anaeróbica + 200m suaves recuperando), descansando 4min entre series.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ffab00',
  }),
];

/* ============================
 *        UMBRALES (7)
 * ============================ */
const umbrales = () => [
  mapRun({
    title: 'Umbrales 3 x 8 min',
    duration: '1:00:00',
    description:
      'Descripción: empieza con 15min de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas. Luego completa 3 x 8min @velocidad de umbral, con 3min de recuperación activa entre intervalos. Termina con 10min de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350' }),
  mapRun({
    title: 'Umbrales 10-10-8 min',
    duration: '1:05:00',
    description:
      'Descripción: empieza con 15min de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas. Luego completa 3 x 8min @velocidad de umbral, con 3min de recuperación activa entre intervalos. Termina con 10min de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350' }),
  mapRun({
    title: 'Umbrales 12-10-10 min',
    duration: '1:10:00',
    description:
      'Descripción: empieza con 15min de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas. Luego completa 12min + 2 x 10min @velocidad de umbral, con 3min de recuperación activa entre intervalos. Termina con 10min de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350' }),
  mapRun({
    title: 'Paso específico 4 x 1200 m + 2 x 1000 m',
    duration: '',
    distance: 14,
    description:
      'Descripción: empieza con 4K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en plano. Luego completa 4 x 1200m@ paso objetivo de 10K + 2 x 1000@ paso ligeramente más rápido (6-8 seg/Km menos) que paso objetivo de 10K, recuperando 50-90% del tiempo de duración del intervalo. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350' }),
  mapRun({
    title: 'afinamiento, 4 x 100m zancadas + paso específico 1200 m',
    duration: '',
    distance: 10,
    description:
      'Descripción: empieza con 5K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en plano. Luego completa 4 x 100m planos @capacidad anaeróbica + 1200m@ paso objetivo de 10K o ligeramente más rápido. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350' }),
  mapRun({
    title: 'Recuperación',
    duration: '',
    distance: 6,
    description: 'Trota suave, que se sienta muy fácil o en zona 1 de FC.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350', tss: 20, durationMin: 40 }),
  mapRun({
    title: 'Activación pre-carrera',
    duration: '',
    distance: 5.4,
    description:
      'Descripción: empieza 5K suaves, que se sientan muy fáciles o en zona 1 de FC. Luego completa 4 x 100m planos duros de activación.',
    icon: 'fa icon-tzone-run',
    bg_color: '#ef5350',
  }, { intensity: 'threshold', bgColor: '#ef5350', tss: 30, durationMin: 45 }),
];

/* ============================
 *          VO2 (6)
 * ============================ */
const vo2 = () => [
  mapRun({
    title: 'VO2max 6 x 3min en subida',
    duration: '1:10:00',
    description:
      'Empieza con 20min de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en subida moderada. Luego completa 6 x 3min @máximo paso sostenible o hasta zona 5b de FC, trotando suave hasta el punto de partida para recuperar. Termina con 10min de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
  mapRun({
    title: 'VO2max 5 x 1000 m',
    duration: '1:10:00',
    description:
      'Descripción: empieza con 3K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en plano. Luego completa 5 x 1000m @ paso de 3-5K, recuperando 50-90% del tiempo de duración del intervalo. Termina con 10min de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
  mapRun({
    title: 'VO2max 5 x 1000 m (200m de capacidad anaeróbica)',
    duration: '',
    distance: 11,
    description:
      'Descripción: empieza con 3K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en plano. Luego completa 5 x 1000m @ paso de 3-5K, pero esta vez incluye dentro de cada intervalo 200m @ capacidad anaeróbica (puede ser al inicio o en cualquier otra sección de los 1000 m), recuperando 50-90% del tiempo de duración del intervalo. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
  mapRun({
    title: 'VO2max 4 x 1200 m + 2 x 1000 m',
    duration: '',
    distance: 13,
    description:
      'Descripción: empieza con 3K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 30m de zancadas poderosas en plano. Luego completa 4 x 1200m@ paso de 5K + 2 x 1000@ paso de 3K, recuperando 50-90% del tiempo de duración del intervalo. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
  mapRun({
    title: 'VO2max 3 x 1000 m + 3 x 800 m',
    duration: '',
    distance: 14,
    description:
      'Descripción: empieza con 5K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 50m de zancadas poderosas en plano. Luego completa 3 x 1000m@ paso de 5K + 3 x 800@ paso de 3K, recuperando 50-90% del tiempo de duración del intervalo. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
  mapRun({
    title: 'VO2max 4 x 800 m + 2 x 600 m',
    duration: '',
    distance: 13,
    description:
      'Descripción: empieza con 5K de calentamiento progresando lentamente de zona 1 a zona 2. Para terminar de activar el cuerpo, completa 4 x 50m de zancadas poderosas en plano. Luego completa 4 x 800m@ paso de 5K + 2 x 600@ paso de 3K, recuperando 50-90% del tiempo de duración del intervalo. Termina con 3K de enfriamiento con trote muy suave.',
    icon: 'fa icon-tzone-run',
    bg_color: '#00c853',
  }, { intensity: 'vo2', bgColor: '#00c853' }),
];

/* ============================
 *         EXPORTS
 * ============================ */
export function getLocalLibrary() {
  return {
    base: baseAerobica(),
    threshold: umbrales(),
    vo2: vo2(),
  };
}

export function localItemToActivityPayload(item, athleteId, dateISO, trainerId) {
  const start = dateISO ? new Date(dateISO) : new Date();
  start.setSeconds(0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);

  const durMin = item.expected?.durationMin ?? 60;
  const distance =
    item.__raw?.distance !== undefined && item.__raw?.distance !== ''
      ? String(item.__raw.distance)
      : '';

  const typeEs = mapSportEs(item.sport || 'correr');
  const theme = getSportTheme(typeEs);

  return {
    assignedTo: athleteId,
    type: typeEs,                           // enum válido del backend
    title: sportTitle(typeEs),              // ej. "Correr"
    trainer: trainerId || null,
    date: start,
    start,
    end,
    description: item.description || '',
    preComments: '',
    postComments: '',
    icon: item.ui?.icon || theme.icon,
    bg_color: (item.ui?.bgColor || theme.color),
    draggable: true,
    external: true,
    planned: {
      duracion: toHMS(durMin),
      distancia: distance,
      calorias: '',
      te: '',
      tss: Math.round(item.expected?.tss ?? 50),
      desnivel_positivo: '',
      ritmo_medio: '',
    },
    completed: {},
  };
}
